import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ReceiptText, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import { Modal } from '@/shared/ui/Modal';
import type { Comprobante, ConceptoDeRetencion, Retencion } from './contrato';

/** Una fila del detalle mientras se arma, antes de guardarla. */
interface Linea {
  clave: number;
  codigoRetencion: string;
  baseImponible: string;
  porcentaje: string;
}

/** El mes que se declara, en MM/AAAA: por defecto el de la factura. */
function periodoDe(fecha: string): string {
  const [anio, mes] = fecha.split('-');
  return `${mes}/${anio}`;
}

function aNumero(texto: string): number {
  const valor = Number(texto.replace(',', '.'));
  return Number.isFinite(valor) ? valor : 0;
}

/**
 * Armar la retención de una compra.
 *
 * <p>Se abre desde el comprobante y no desde una pantalla aparte porque ahí ya está todo el
 * contexto: el proveedor, las bases y el IVA que trajo su factura. Empezar desde cero obligaría
 * a buscar la compra y a copiar los importes a mano, que es de donde salen los errores.
 *
 * <p>El valor retenido <b>no se escribe</b>: sale de la base por el porcentaje. Es lo que el
 * proveedor se acredita en su declaración, y el servidor lo vuelve a calcular por su cuenta —aquí
 * solo se enseña para que se vea antes de guardar.
 */
export function DialogoDeRetencion({
  compra,
  alCerrar,
}: {
  compra: Comprobante;
  alCerrar: () => void;
}) {
  const [periodo, setPeriodo] = useState(() => periodoDe(compra.fechaEmision));
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [siguiente, setSiguiente] = useState(1);

  const clienteDeConsultas = useQueryClient();

  const conceptos = useQuery({
    queryKey: ['conceptos-vigentes', compra.fechaEmision],
    queryFn: () =>
      api.get<ConceptoDeRetencion[]>('/retenciones/conceptos/vigentes', {
        query: { fecha: compra.fechaEmision },
      }),
  });

  const porCodigo = useMemo(
    () => new Map((conceptos.data ?? []).map((c) => [c.codigo, c])),
    [conceptos.data],
  );

  /** La base que suele tocar: el subtotal para renta, el IVA de la factura para IVA. */
  function baseSugerida(codigo: string): string {
    const concepto = porCodigo.get(codigo);
    if (!concepto) {
      return '';
    }
    const base =
      concepto.impuesto === '2'
        ? (compra.importes.iva ?? 0)
        : (compra.importes.subtotalSinImpuestos ?? 0);
    return base > 0 ? base.toFixed(2) : '';
  }

  function agregar() {
    setLineas((previas) => [
      ...previas,
      { clave: siguiente, codigoRetencion: '', baseImponible: '', porcentaje: '' },
    ]);
    setSiguiente((n) => n + 1);
  }

  function cambiar(clave: number, campo: keyof Linea, valor: string) {
    setLineas((previas) =>
      previas.map((l) => {
        if (l.clave !== clave) {
          return l;
        }
        if (campo === 'codigoRetencion') {
          const concepto = porCodigo.get(valor);
          // Al elegir el concepto se traen su porcentaje y la base que suele corresponder; las
          // dos se pueden corregir, porque hay conceptos sin porcentaje fijo.
          return {
            ...l,
            codigoRetencion: valor,
            porcentaje: concepto?.porcentaje != null ? String(concepto.porcentaje) : '',
            baseImponible: l.baseImponible || baseSugerida(valor),
          };
        }
        return { ...l, [campo]: valor };
      }),
    );
  }

  const total = lineas.reduce(
    (suma, l) => suma + (aNumero(l.baseImponible) * aNumero(l.porcentaje)) / 100,
    0,
  );

  const completas = lineas.filter(
    (l) => l.codigoRetencion !== '' && aNumero(l.baseImponible) > 0 && l.porcentaje !== '',
  );
  const lista = completas.length === lineas.length && lineas.length > 0 && /^\d{2}\/\d{4}$/.test(periodo);

  const guardar = useMutation({
    mutationFn: () =>
      api.post<Retencion>('/retenciones', {
        comprobanteId: compra.id,
        periodoFiscal: periodo,
        fechaEmision: new Date().toISOString().slice(0, 10),
        lineas: lineas.map((l) => ({
          codigoRetencion: l.codigoRetencion,
          baseImponible: aNumero(l.baseImponible),
          porcentaje: aNumero(l.porcentaje),
        })),
      }),
    onSuccess: (r) => {
      notificar.exito(
        `Quedó registrada por $${r.totalRetenido.toFixed(2)}`,
        'Retención en borrador',
      );
      void clienteDeConsultas.invalidateQueries({ queryKey: ['retenciones'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['compras'] });
      alCerrar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo registrar'),
  });

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      icono={ReceiptText}
      titulo="Retener de esta compra"
      subtitulo={`${compra.proveedorRazonSocial} · ${compra.numero}`}
      ancho="ancho"
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Resumen rotulo="Subtotal" valor={compra.importes.subtotalSinImpuestos ?? 0} />
          <Resumen rotulo="IVA de la factura" valor={compra.importes.iva ?? 0} />
          <Resumen rotulo="Total" valor={compra.importes.total ?? 0} />
        </div>

        <label className="block">
          <span className="block text-xs font-bold uppercase tracking-wide text-armazon-700">
            Período fiscal
          </span>
          <span className="mt-0.5 block text-[11px] uppercase tracking-wide text-armazon-500">
            El mes que se declara. No tiene que ser el de la factura: una compra de fin de mes
            puede retenerse en el siguiente.
          </span>
          <input
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            placeholder="MM/AAAA"
            maxLength={7}
            className="importe mt-1.5 h-10 w-32 rounded-xl border border-armazon-300 px-3 text-sm"
          />
        </label>

        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wide text-armazon-700">
              Qué se retiene
            </span>
            <button
              type="button"
              onClick={agregar}
              className="inline-flex items-center gap-1 rounded-lg border border-armazon-300 bg-white px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-armazon-600 transition hover:bg-armazon-100"
            >
              <Plus className="size-3.5" aria-hidden />
              Añadir
            </button>
          </div>

          {lineas.length === 0 ? (
            <p className="mt-2 rounded-xl border border-dashed border-armazon-300 px-4 py-6 text-center text-[11px] uppercase tracking-wide text-armazon-400">
              Añada al menos un concepto
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {lineas.map((l) => {
                const retenido = (aNumero(l.baseImponible) * aNumero(l.porcentaje)) / 100;
                return (
                  <li
                    key={l.clave}
                    className="grid grid-cols-[1fr_auto] gap-2 rounded-xl border border-armazon-200 p-3 sm:grid-cols-[1fr_7rem_5rem_6rem_auto]"
                  >
                    <select
                      value={l.codigoRetencion}
                      onChange={(e) => cambiar(l.clave, 'codigoRetencion', e.target.value)}
                      aria-label="Concepto de retención"
                      className="col-span-2 h-9 min-w-0 rounded-lg border border-armazon-300 px-2 text-xs sm:col-span-1"
                    >
                      <option value="">Elija el concepto</option>
                      {(conceptos.data ?? []).map((c) => (
                        <option key={c.id} value={c.codigo}>
                          {c.impuesto === '1' ? 'Renta' : 'IVA'} · {c.codigo} · {c.descripcion}
                        </option>
                      ))}
                    </select>

                    <input
                      value={l.baseImponible}
                      onChange={(e) => cambiar(l.clave, 'baseImponible', e.target.value)}
                      inputMode="decimal"
                      placeholder="Base"
                      aria-label="Base imponible"
                      className="importe h-9 rounded-lg border border-armazon-300 px-2 text-right text-xs"
                    />
                    <input
                      value={l.porcentaje}
                      onChange={(e) => cambiar(l.clave, 'porcentaje', e.target.value)}
                      inputMode="decimal"
                      placeholder="%"
                      aria-label="Porcentaje"
                      className="importe h-9 rounded-lg border border-armazon-300 px-2 text-right text-xs"
                    />

                    <span className="importe flex h-9 items-center justify-end text-xs font-bold text-armazon-800">
                      ${retenido.toFixed(2)}
                    </span>

                    <button
                      type="button"
                      onClick={() => setLineas((p) => p.filter((o) => o.clave !== l.clave))}
                      aria-label="Quitar"
                      className="grid size-9 place-items-center rounded-lg text-armazon-400 transition hover:bg-red-50 hover:text-alerta"
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between rounded-xl bg-armazon-50 px-4 py-3">
          <span className="text-xs font-bold uppercase tracking-wide text-armazon-600">
            Total a retener
          </span>
          <span className="importe text-lg font-extrabold text-armazon-900">
            ${total.toFixed(2)}
          </span>
        </div>

        <p className="text-[11px] uppercase leading-relaxed tracking-wide text-armazon-500">
          Queda en borrador. Después se envía al SRI desde la pantalla de retenciones, y hasta
          entonces se puede corregir sin consecuencias.
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={alCerrar}
            className="rounded-xl px-4 py-2 text-xs font-bold uppercase tracking-wide text-armazon-600 hover:bg-armazon-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!lista || guardar.isPending}
            onClick={() => guardar.mutate()}
            className="rounded-xl bg-marca-600 px-5 py-2 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-marca-700 disabled:opacity-50"
          >
            {guardar.isPending ? 'Guardando' : 'Registrar la retención'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Resumen({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <div className="rounded-xl border border-armazon-200 px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-armazon-400">{rotulo}</p>
      <p className="importe mt-0.5 text-sm font-bold text-armazon-800">${valor.toFixed(2)}</p>
    </div>
  );
}
